/**
 * Orders API client
 *
 * Stream 2 Phase 2-1b / Plan §10 Stream 2 / Design §4.2 A 履约.
 *
 * 包装 axios apiClient 调用 admin-service /orders 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。所有写动作通过 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';

export type OrderStatus =
  | 'pending_payment'
  | 'paid'
  | 'shipped'
  | 'completed'
  | 'cancelled';

export interface OrderListItem {
  orderId: string;
  userId: string;
  workId: string;
  status: OrderStatus;
  paymentStatus: string;
  logisticsStatus: string;
  totalAmount: number; // 单位：分
  receiverName: string;
  receiverPhoneMasked: string; // 已脱敏
  paidAt: string | null;
  shippedAt: string | null;
  createdAt: string;
}

export interface ListOrdersResponse {
  items: OrderListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListOrdersFilter {
  status?: OrderStatus;
  paymentStatus?: string;
  logisticsStatus?: string;
  userId?: string;
  workId?: string;
  fromDate?: string; // ISO
  toDate?: string;   // ISO
  page?: number;
  pageSize?: number;
}

export interface OrderDetail {
  order: {
    orderId: string;
    userId: string;
    workId: string;
    status: OrderStatus;
    paymentStatus: string;
    logisticsStatus: string;
    totalAmount: number;
    receiverName: string;
    receiverPhone: string; // 后端按 actor 是否有 pii:read_phone 决定明文 / 脱敏
    receiverProvince: string;
    receiverCity: string;
    receiverDistrict: string;
    receiverAddress: string; // 后端按 pii:read_address 决定
    paidAt: string | null;
    shippedAt: string | null;
    cancelledAt: string | null;
    adminNote: string | null;
    createdAt: string;
    updatedAt: string;
  };
  payment: {
    paymentRecordId: string;
    payerUserId: string;
    amount: number;
    paymentMethod: string;
    status: string;
    paidAt: string | null;
    refundedAt: string | null;
    refundAmount: number | null;
    refundStatus: string | null;
  } | null;
  logistics: {
    logisticsRecordId: string;
    status: string;
    company: string | null;
    trackingNumber: string | null;
    trackingEvents: unknown;
    shippedAt: string | null;
  } | null;
  work: {
    workId: string;
    title: string;
    ownerUserId: string;
    isBookUnlocked: boolean;
  } | null;
  isSubstitutePayment: boolean;
}

export interface ShipOrderInput {
  trackingNumber: string;
  company: string;
  shippedAt?: string;
}

export interface UpdateOrderAddressInput {
  receiver_name?: string;
  receiver_phone?: string;
  receiver_province?: string;
  receiver_city?: string;
  receiver_district?: string;
  receiver_address?: string;
}

export interface CancelOrderInput {
  reason: string;
}

export interface AddOrderNoteInput {
  note: string;
}

export interface RefundSubstituteInput {
  refundAmount: number; // 单位：分
  reason: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /orders — 订单列表查询（分页 + 多字段过滤）。 */
export async function listOrders(
  filter: ListOrdersFilter = {},
): Promise<ListOrdersResponse> {
  const params: Record<string, string | number> = {};
  if (filter.status) params.status = filter.status;
  if (filter.paymentStatus) params.paymentStatus = filter.paymentStatus;
  if (filter.logisticsStatus) params.logisticsStatus = filter.logisticsStatus;
  if (filter.userId) params.userId = filter.userId;
  if (filter.workId) params.workId = filter.workId;
  if (filter.fromDate) params.fromDate = filter.fromDate;
  if (filter.toDate) params.toDate = filter.toDate;
  if (filter.page != null) params.page = filter.page;
  if (filter.pageSize != null) params.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<ListOrdersResponse>>('/orders', {
    params,
  });
  return resp.data.data;
}

/** GET /orders/:id — 订单详情（含 payment / logistics / work / isSubstitutePayment）。 */
export async function getOrder(id: string): Promise<OrderDetail> {
  const resp = await apiClient.get<ServiceResponse<OrderDetail>>(
    `/orders/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** POST /orders/:id/ship — 录入运单号（ORDER_UPDATE_LOGISTICS）。 */
export async function shipOrder(id: string, input: ShipOrderInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/orders/${encodeURIComponent(id)}/ship`,
    input,
  );
}

/** PATCH /orders/:id/address — 修改收件地址（ORDER_UPDATE_LOGISTICS）。 */
export async function updateOrderAddress(
  id: string,
  input: UpdateOrderAddressInput,
): Promise<void> {
  await apiClient.patch<ServiceResponse<null>>(
    `/orders/${encodeURIComponent(id)}/address`,
    input,
  );
}

/** POST /orders/:id/cancel — 取消订单（ORDER_UPDATE_LOGISTICS；仅 pending_payment 状态）。 */
export async function cancelOrder(id: string, input: CancelOrderInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/orders/${encodeURIComponent(id)}/cancel`,
    input,
  );
}

/** POST /orders/:id/note — 新增 / 更新管理员备注（ORDER_UPDATE_LOGISTICS）。 */
export async function addOrderNote(id: string, input: AddOrderNoteInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/orders/${encodeURIComponent(id)}/note`,
    input,
  );
}

/**
 * POST /orders/:id/refund_substitute — 替支付反悔退款（ORDER_REVERT_SUBSTITUTE）。
 * 后端服务端生成 workId / outRefundNo，前端只传 refundAmount（分）+ reason。
 */
export async function refundSubstitute(
  id: string,
  input: RefundSubstituteInput,
): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/orders/${encodeURIComponent(id)}/refund_substitute`,
    input,
  );
}
